import type { UserPreferences, PersistenceAPI } from '@atlaskit/editor-common/user-preferences';
import {
	UserPreferencesProvider,
	TOOLBAR_DOCKING_POSITIONS,
} from '@atlaskit/editor-common/user-preferences';

import { type UseReadFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import EditorPreferencesQuery from './__generated__/EditorPreferencesQuery.graphql.ts';
import EditorPreferencesMutation from './__generated__/EditorPreferencesMutation.graphql.ts';
import type {
	EditorPreferencesQuery$data,
	EditorPreferencesQuery$variables,
} from './__generated__/EditorPreferencesQuery.graphql.ts';
import type {
	EditorPreferencesMutation$data,
	EditorPreferencesMutation$variables,
} from './__generated__/EditorPreferencesMutation.graphql.ts';
// eslint-disable-next-line import/no-cycle
import { isSSR, LOCAL_STORAGE_KEY } from './EditorUserPreferencesProvider.ts';
import { expVal } from '@confluence/feature-experiments';

const parseConfluenceEditorSettings = (
	confluenceEditorSettings:
		| NonNullable<EditorPreferencesQuery$data['userPreferences']>['confluenceEditorSettings']
		| null
		| undefined,
) => {
	if (confluenceEditorSettings) {
		const result: Record<string, any> = {};
		const entries = Object.entries(confluenceEditorSettings);
		for (const [key, value] of entries) {
			if (
				key === 'toolbarDockingInitialPosition' &&
				value &&
				(TOOLBAR_DOCKING_POSITIONS as readonly string[]).includes(value)
			) {
				result.toolbarDockingPosition = value as 'top' | 'none';
			} else if (value !== null && value !== undefined) {
				// Pass through other settings as-is
				result[key] = value;
			}
		}
		return result as UserPreferences;
	}

	return {} as UserPreferences;
};

const updateLocalStorage = (userPreferences: UserPreferences) => {
	// confluenceLocalStorageInstance is only available in the browser environment
	if (!isSSR() && confluenceLocalStorageInstance) {
		confluenceLocalStorageInstance.setItem(LOCAL_STORAGE_KEY, JSON.stringify(userPreferences));
	}
};

export class UserPreferencesPersistenceAPI implements PersistenceAPI {
	private readFragmentInterop: UseReadFragmentInteropResult<any> | undefined;
	private isLoggedin = true;
	private isInitialLoad = false;

	public constructor(isLoggedin = true, readFragmentInterop?: UseReadFragmentInteropResult<any>) {
		this.setIsLoggedIn(isLoggedin);
		this.readFragmentInterop = readFragmentInterop;
	}

	public setIsLoggedIn(isLoggedin: boolean) {
		this.isLoggedin = isLoggedin;
	}

	public getInitialUserPreferences(): UserPreferences | undefined {
		let initialUserPreferences: UserPreferences | undefined;

		try {
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true) &&
				this.readFragmentInterop
			) {
				const result = this.readFragmentInterop({
					id: `$ROOT_QUERY.userPreferences`,
				}) as EditorPreferencesQuery$data['userPreferences'];

				if (result) {
					initialUserPreferences = parseConfluenceEditorSettings(result.confluenceEditorSettings);
				}
			} else {
				const result = readQueryInterop<
					EditorPreferencesQuery$data,
					EditorPreferencesQuery$variables
				>({
					query: EditorPreferencesQuery,
				});

				if (result?.userPreferences) {
					initialUserPreferences = parseConfluenceEditorSettings(
						result.userPreferences?.confluenceEditorSettings,
					);
				}
			}
		} catch {
			initialUserPreferences = undefined;
		}

		// If we don't have values from the Apollo cache, we try to fetch from local storage
		if (!initialUserPreferences && !isSSR()) {
			try {
				const userPreferenceStorage = confluenceLocalStorageInstance.getItem(LOCAL_STORAGE_KEY);
				if (userPreferenceStorage) {
					initialUserPreferences = JSON.parse(userPreferenceStorage);
				}
			} catch {
				// If the local storage is corrupted or not available, we ignore the error and return undefined
				initialUserPreferences = undefined;
			}
		}

		return initialUserPreferences;
	}

	public async updateUserPreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	) {
		if (!this.isLoggedin) {
			// If the user is not logged in, we return the key with updated value
			// This is to ensure that the editor can still function without flaws
			// but the value will not be persisted to the server
			return Promise.resolve({ [key]: value } as Pick<UserPreferences, K>);
		}

		// we only support updating the toolbarDockingInitialPosition preference ATM,
		// need to update this if we add more preferences
		const variables = {
			toolbarDockingInitialPosition: value,
		} as EditorPreferencesMutation$variables;

		try {
			const { data } = await mutateInterop<
				EditorPreferencesMutation$data,
				EditorPreferencesMutation$variables
			>({
				mutation: EditorPreferencesMutation,
				variables,
			});

			// this is required to indicate toolbar changes to any useQuery for EditorPreferencesQuery, particularly progressive object header
			writeQueryInterop({
				query: EditorPreferencesQuery,
				data: {
					userPreferences: {
						__typename: 'UserPreferences',
						confluenceEditorSettings: {
							toolbarDockingInitialPosition: value ?? null,
						},
					},
				},
			});

			const userPreferences = parseConfluenceEditorSettings(
				data?.updateUserPreferences?.confluenceEditorSettings,
			);

			updateLocalStorage(userPreferences);

			return { [key]: userPreferences[key] } as Pick<UserPreferences, K>;
		} catch {
			// In case of an error, we return the key with updated value
			// This is to ensure that the editor can still function without flaws
			// but the value might not be persisted to the server
			return { [key]: value } as Pick<UserPreferences, K>;
		}
	}

	public async loadUserPreferences(): Promise<UserPreferences> {
		let userPreferences = {} as UserPreferences;

		if (!this.isLoggedin) {
			// If the user is not logged in, we return an empty object
			return Promise.resolve(userPreferences);
		}

		try {
			// first time we load we use cache-first policy to avoid unnecessary network requests
			// after the first load we always use no-cache to ensure we have the latest preferences
			// this is particularly important when user has multiple tabs open and changes preferences in one tab
			// we want to ensure other tabs have the latest preferences
			const fetchPolicy =
				!this.isInitialLoad &&
				expVal('cc_editor_preferences_query_optimisation', 'isEnabled', false)
					? 'cache-first'
					: 'no-cache';
			this.isInitialLoad = true;

			const { data } = await queryInterop<EditorPreferencesQuery$data>({
				query: EditorPreferencesQuery,
				fetchPolicy,
			});

			userPreferences = parseConfluenceEditorSettings(
				data?.userPreferences?.confluenceEditorSettings,
			);

			updateLocalStorage(userPreferences);
			return userPreferences;
		} catch {
			// if upload local storage fails, we will still return the user preferences
			return userPreferences;
		}
	}
}

export const getUserPreferencesProvider = (options: {
	isLivePage: boolean;
	persistenceAPI?: UserPreferencesPersistenceAPI;
	readFragmentInterop?: UseReadFragmentInteropResult<any>;
}) => {
	const persistenceAPI =
		options.persistenceAPI ||
		new UserPreferencesPersistenceAPI(undefined, options.readFragmentInterop);

	return new UserPreferencesProvider(persistenceAPI, {
		toolbarDockingPosition: options.isLivePage ? 'none' : 'top',
	});
};
